export const MaterialConsumptionModes = [
    { name: 'Whole Sheet', value: 'WholeSheet' },
    { name: 'Nest Bounds', value: 'NestBounds' },
]

// QM-3455: renamed labels shown to organisations created after rollout, gated by the
// `showRenamedConsumptionModes` treatment. Backend values are unchanged — display only.
export const RenamedMaterialConsumptionModes = [
    { name: 'Parts per sheet', value: 'WholeSheet' },
    { name: 'Best yield', value: 'NestBounds' },
]

export const RotaryMaterialConsumptionModes = [
    { name: 'Parts per length', value: 'WholeSheet' },
    { name: 'Part bounds', value: 'NestBounds' },
    { name: 'Full tube lengths', value: 'FullTubeLengths' },
]

/**
 * Returns the consumption-mode options for a material.
 *
 * @param {boolean} isRotary - Whether the material is rotary (tube)
 * @param {boolean} [useRenamedModes=false] - When true (and not rotary), returns the QM-3455 renamed labels
 * @returns {Array<{name: string, value: string}>}
 */
export function getMaterialConsumptionModes(isRotary, useRenamedModes = false) {
    if (isRotary) {
        return RotaryMaterialConsumptionModes
    }
    return useRenamedModes ? RenamedMaterialConsumptionModes : MaterialConsumptionModes
}

/**
 * Maps a consumption-mode value to its display label.
 *
 * @param {boolean} isRotary - Whether the material is rotary (tube)
 * @param {string} value - The backend consumption-mode value (e.g. 'WholeSheet')
 * @param {boolean} [useRenamedModes=false] - When true (and not rotary), returns the QM-3455 renamed label
 * @returns {string} The display label (falls back to the first mode when value is unknown)
 */
export function getMaterialConsumptionName(isRotary, value, useRenamedModes = false) {
    const modes = getMaterialConsumptionModes(isRotary, useRenamedModes)
    return (modes.find((mode) => mode.value === value) ?? modes[0]).name
}

/**
 * Returns a user-facing label describing which nesting algorithm applies to a quote item.
 *
 * Unlike `getMaterialConsumptionName` (which produces dropdown labels such as "Nest Bounds"),
 * this returns marketing-friendly terminology shown in the nest popup and icon tooltip.
 *
 * Source-of-truth rules — keep these in mind before refactoring:
 *
 * - **Sheet items** derive their label from `organisation.nestingAlgorithmToUse` (a 2-value
 *   enum: `'Toolpath'` or `'Rectangular'`). The popup pairs the label with a "Review your
 *   nesting settings" link that sends the user to the org-defaults page where the same field
 *   is set; the label must agree with what the user will see selected there. Do NOT derive
 *   sheet labels from `materialConsumptionMode` (a billing field) or from `nestImageUri` (a
 *   downstream artifact) — both proxies have shipped wrong on this code path before.
 * - When `nestingAlgorithmToUse === 'Toolpath'`, the org-level boolean
 *   `rectangularNestingForSingularParts` can flip individual items with `quantity === 1`
 *   back to Rectangular Bounds. The checkbox is disabled in the org UI unless Toolpath is
 *   selected, so this flag is only meaningful inside the True-Shape regime.
 * - **Rotary items** derive their label from `materialConsumptionMode` because rotary has no
 *   equivalent org-level algorithm setting; the consumption mode IS the algorithm for tubes.
 *
 * @param {Object} [options]
 * @param {boolean} [options.isRotary=false] - Whether the quote item uses a rotary (tube) material
 * @param {string} [options.nestingAlgorithmToUse] - Org setting: 'Toolpath' | 'Rectangular'. Sheet branch only.
 * @param {boolean} [options.rectangularNestingForSingularParts] - Org flag: when true and `quantity === 1`, sheets fall back to Rectangular Bounds even if `nestingAlgorithmToUse` is Toolpath
 * @param {number} [options.quantity] - Quote item quantity. Used with the singular-parts flag.
 * @param {string} [options.materialConsumptionMode] - 'WholeSheet' | 'NestBounds' | 'FullTubeLengths'. Rotary branch only.
 * @returns {string} Translation key for the nesting method label
 */
export function getNestingMethodLabel({
    isRotary = false,
    materialConsumptionMode,
    nestingAlgorithmToUse,
    quantity,
    rectangularNestingForSingularParts,
} = {}) {
    if (isRotary) {
        if (materialConsumptionMode === 'FullTubeLengths') {
            return 'Full tube lengths'
        }
        if (materialConsumptionMode === 'NestBounds') {
            return 'Part Bounds nesting'
        }
        return 'Parts per length'
    }

    if (nestingAlgorithmToUse === 'Rectangular') {
        return 'Rectangular Bounds nesting'
    }
    if (rectangularNestingForSingularParts && quantity === 1) {
        return 'Rectangular Bounds nesting'
    }
    return 'True Shape nesting'
}
